import { css } from "@emotion/css";
import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { InfoCircleIcon, ParentEnvironmentIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { EphemeralEnvironmentsOnboardingEnvironment, GetEphemeralEnvironmentsOnboardingDataBffResponse } from "@octopusdeploy/octopus-server-client";
import { isEmpty } from "lodash";
import React from "react";
import { Note, Select, Text } from "~/components/form";
import parentEnvironmentInfoImage from "../assets/parent-environment-info.svg";
import { ConfigWizardHelpLayout } from "./ConfigWizardHelpLayout";
import { ConfigWizardStatusBar } from "./ConfigWizardStatusBar";
import type { ParentEnvironmentConfig } from "./EphemeralEnvironmentsConfigWizard";
export function ParentEnvironmentHelp() {
    return (<ConfigWizardHelpLayout title="Parent environment">
            Ephemeral environments are created based on a parent environment, inheriting its scoped deployment targets, variables, and accounts. The parent environment also defines the auto deprovisioning settings, which default to 7 days of
            inactivity.
        </ConfigWizardHelpLayout>);
}
export const parentEnvironmentHelpImage = { src: parentEnvironmentInfoImage, altText: "Graphic of ephemeral environments inheriting from a parent environment" };
interface ParentEnvironmentContentProps {
    data: GetEphemeralEnvironmentsOnboardingDataBffResponse;
    parentEnvironmentConfig: ParentEnvironmentConfig;
    setParentEnvironmentConfig: (config: ParentEnvironmentConfig) => void;
    isCommittingRunbooksToNewBranch: boolean;
}
export function ParentEnvironmentContent({ data, parentEnvironmentConfig, setParentEnvironmentConfig, isCommittingRunbooksToNewBranch }: ParentEnvironmentContentProps) {
    const noExistingEnvironments = isEmpty(data.ParentEnvironments);
    const canCreate = data.Permissions.CreateEnvironments;
    const cannotCreateNote = !canCreate && <Note>You do not have permission to create a new parent environment</Note>;
    return (<div>
            <ConfigWizardStatusBar activeStage="Environment" isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>
            <h3>Parent Environment</h3>
            {noExistingEnvironments ? (<>
                    <CreateParentEnvironmentTextField value={parentEnvironmentConfig?.name || ""} onChange={(value) => setParentEnvironmentConfig({ ...parentEnvironmentConfig, name: value })} disabled={!canCreate}/>
                    {cannotCreateNote}
                </>) : (<RadioButtonGroup value={parentEnvironmentConfig.action} onChange={(value) => {
                setParentEnvironmentConfig({ id: undefined, name: undefined, action: value });
            }}>
                    <RadioButton key="select" label="Use an existing parent environment" value="Select"/>
                    {parentEnvironmentConfig.action == "Select" && (<SelectParentEnvironmentCombo parentEnvironments={data.ParentEnvironments} parentEnvironmentConfig={parentEnvironmentConfig} setParentEnvironmentConfig={setParentEnvironmentConfig}/>)}
                    <RadioButton key="create" label="Add a new parent environment" value="Create" disabled={!canCreate}/>
                    {cannotCreateNote}
                    {parentEnvironmentConfig.action == "Create" && (<CreateParentEnvironmentTextField value={parentEnvironmentConfig?.name || ""} onChange={(value) => setParentEnvironmentConfig({ ...parentEnvironmentConfig, name: value })} disabled={!canCreate}/>)}
                </RadioButtonGroup>)}
        </div>);
}
interface SelectParentEnvironmentComboProps {
    parentEnvironments: EphemeralEnvironmentsOnboardingEnvironment[];
    parentEnvironmentConfig: ParentEnvironmentConfig;
    setParentEnvironmentConfig: (config: ParentEnvironmentConfig) => void;
}
function SelectParentEnvironmentCombo({ parentEnvironments, parentEnvironmentConfig, setParentEnvironmentConfig }: SelectParentEnvironmentComboProps) {
    const styles = {
        autocomplete: css({
            maxWidth: 400,
            width: "100%",
        }),
    };
    return (<div className={styles.autocomplete}>
            <Select label="Parent environment" value={parentEnvironmentConfig?.id || ""} items={parentEnvironments.map((env) => ({ value: env.Id, text: env.Name, icon: <ParentEnvironmentIcon size={16}/> }))} onChange={(id) => {
            if (!id) {
                setParentEnvironmentConfig({ ...parentEnvironmentConfig, id: undefined, name: undefined });
                return;
            }
            const envs = parentEnvironments.filter((env) => env.Id === id);
            if (envs.length !== 1) {
                throw new Error(`Expected exactly one parent environment with Id=${id}, but found ${envs.length}`);
            }
            setParentEnvironmentConfig({ ...parentEnvironmentConfig, id: id, name: envs[0].Name });
        }}/>
        </div>);
}
interface CreateParentEnvironmentTextFieldProps {
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
}
function CreateParentEnvironmentTextField({ value, onChange, disabled }: CreateParentEnvironmentTextFieldProps) {
    const style = {
        helperText: css({
            display: "inline-flex",
            gap: space[4],
            fontSize: fontSize.small,
            alignItems: "center",
        }),
    };
    return (<div>
            <Text label="Parent environment name" value={value} onChange={(ref) => onChange(ref)} disabled={disabled}/>
            <div className={style.helperText}>
                <InfoCircleIcon size={16} color={themeTokens.color.icon.info}/> Auto deprovisioning is set to <strong>7 days</strong> by default for new parent environments.
            </div>
        </div>);
}
